import { ReactElement } from 'react'
import { useFormContext } from 'react-hook-form'
import { get } from 'lodash-es'
import { Divider, Grid, InputAdornment, MenuItem, Stack } from '@mui/material'
import { MagicButton } from '@ui/actions'
import { ErrorFieldBase, TextField, Select, UnitConvertedField } from '@ui/forms'
import { useModelStore } from '@editorStores'
import {
  isSimplifiedWindLoadMethod,
  windLoadComputationMethodOptions,
} from '../../../../utils/windLoadFormUtils'
import BuildingDimensionsInput from './components/BuildingDimensionsInput'
import BuildingDimensionsSceneMarkers from './components/BuildingDimensionsSceneMarkers'
import {
  windZoneOptions,
  terrainCategoryOptions,
  windZoneToPressureProposal,
  windZoneToSpeedProposal,
} from './constants'
import {
  getSimplifiedMethodStandortOptions,
  lookupSimplifiedGustPressureQp,
  normalizeSimplifiedMethodStandort,
} from './simplifiedMethodQpLookup'
import {
  calculateBuildingDimensions,
  calculateWindBoxConfig,
  hasAssignedWalls,
  hasCompleteRoofAssignment,
} from './utils'

const BaseParameterFields = (): ReactElement => {
  const {
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext()
  const { model } = useModelStore()
  const wallAssignment = watch('wallAssignment') as WindLoadWallAssignment
  const baseParameters = watch('baseParameters') as WindLoadParameters
  const simplifiedMethodStandort = watch(
    'baseParameters.simplifiedMethodStandort',
  ) as SimplifiedMethodStandort
  const computationMethod = baseParameters?.computationMethod ?? 'GroundRoughnessConsidered'
  const isSimplifiedMethod = isSimplifiedWindLoadMethod(computationMethod)
  const windZone = baseParameters?.wind_zone ?? 1
  const standortOptions = getSimplifiedMethodStandortOptions(windZone)
  const roofAssignment = watch('roofAssignment') as Partial<
    Record<WindDirection, { configurations?: RoofConfig[] }>
  >
  const roofBuildingDimensions = watch('roofBuildingDimensions') as
    | BuildingDimensionsDataWithStart
    | undefined
  const qZoneBuildingDimensions = watch('qZoneBuildingDimensions') as
    | BuildingDimensionsDataWithStart
    | undefined
  const wallBuildingDimensions = watch('wallBuildingDimensions') as
    | BuildingDimensionsDataWithStart
    | undefined
  const wallAssignmentError = get(errors, 'recalculateDimensions.wallAssignment.message') as
    | string
    | undefined
  const roofAssignmentError = get(errors, 'recalculateDimensions.roofAssignment.message') as
    | string
    | undefined

  const applySimplifiedMethodQpProposal = (
    targetWindZone: WindZone,
    buildingHeightForQpLookup: number,
    standort: SimplifiedMethodStandort = normalizeSimplifiedMethodStandort(
      targetWindZone,
      simplifiedMethodStandort,
    ),
  ) => {
    const proposedQp = lookupSimplifiedGustPressureQp(
      targetWindZone,
      buildingHeightForQpLookup,
      standort,
    )
    if (proposedQp !== null) {
      setValue('baseParameters.gustPressure_q_p', proposedQp, { shouldDirty: true })
    }
  }

  const handleRecalculateDimensions = () => {
    const hasWallsAssigned = hasAssignedWalls(wallAssignment)
    const hasCompleteRoofConfig = hasCompleteRoofAssignment(roofAssignment)

    clearErrors('recalculateDimensions')

    if (!hasWallsAssigned) {
      setError('recalculateDimensions.wallAssignment', {
        type: 'manual',
        message: 'Wände müssen erst ausgewählt werden',
      })
    }

    if (!hasCompleteRoofConfig) {
      setError('recalculateDimensions.roofAssignment', {
        type: 'manual',
        message: 'Dachflächen müssen erst zugewiesen werden',
      })
    }

    if (!hasWallsAssigned || !hasCompleteRoofConfig) return

    const computedDimensions = calculateBuildingDimensions(model, wallAssignment)
    if (computedDimensions.roofDimensions) {
      setValue('roofBuildingDimensions', computedDimensions.roofDimensions, {
        shouldDirty: true,
      })
    }
    if (computedDimensions.wallDimensions) {
      setValue('wallBuildingDimensions', computedDimensions.wallDimensions, {
        shouldDirty: true,
      })
      setValue('windBoxConfig', calculateWindBoxConfig(computedDimensions.wallDimensions), {
        shouldDirty: true,
      })
    }
    if (!isSimplifiedMethod && computedDimensions.qZoneDimensions) {
      setValue('qZoneBuildingDimensions', computedDimensions.qZoneDimensions, {
        shouldDirty: true,
      })
    }
    if (isSimplifiedMethod && computedDimensions.wallDimensions) {
      const buildingHeightForQpLookup = computedDimensions.wallDimensions.height
      setValue('baseParameters.buildingHeightFor_q_p_lookup', buildingHeightForQpLookup, {
        shouldDirty: true,
      })
      applySimplifiedMethodQpProposal(baseParameters?.wind_zone ?? 1, buildingHeightForQpLookup)
    }
  }

  const handleComputationMethodChange = (nextMethod: WindLoadComputationMethod) => {
    setValue('baseParameters.computationMethod', nextMethod, { shouldDirty: true })
    if (nextMethod === 'SimplifiedMethod') {
      const windZone = baseParameters?.wind_zone ?? 1
      const buildingHeightForQpLookup =
        wallBuildingDimensions?.height ?? baseParameters?.buildingHeightFor_q_p_lookup ?? 10
      setValue('baseParameters.buildingHeightFor_q_p_lookup', buildingHeightForQpLookup, {
        shouldDirty: true,
      })
      applySimplifiedMethodQpProposal(windZone, buildingHeightForQpLookup)
    }
    if (nextMethod === 'GroundRoughnessConsidered') {
      const windZone = baseParameters?.wind_zone ?? 1
      setValue('baseParameters.wind_speed', windZoneToSpeedProposal[windZone], {
        shouldDirty: true,
      })
      setValue('baseParameters.baseWindPressure_q_b', windZoneToPressureProposal[windZone], {
        shouldDirty: true,
      })
    }
  }

  return (
    <>
      <BuildingDimensionsSceneMarkers
        wallDimensions={wallBuildingDimensions}
        roofDimensions={roofBuildingDimensions}
        qZoneDimensions={isSimplifiedMethod ? undefined : qZoneBuildingDimensions}
      />
      <Stack direction="column" spacing={1}>
        <MagicButton
          onClick={handleRecalculateDimensions}
          data-cy="btn-wind-load-recalculate-building-dimensions"
        >
          Gebäudedimensionen neu berechnen
        </MagicButton>
        {wallAssignmentError && <ErrorFieldBase message={wallAssignmentError} severity="error" />}
        {roofAssignmentError && <ErrorFieldBase message={roofAssignmentError} severity="error" />}
        <TextField
          select
          label="Berechnungsverfahren"
          name="baseParameters.computationMethod"
          size="small"
          data-cy="select-wind-load-computation-method"
          onChange={event => {
            handleComputationMethodChange(event.target.value as WindLoadComputationMethod)
          }}
        >
          {windLoadComputationMethodOptions.map(({ value, label }) => (
            <MenuItem value={value} key={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <BuildingDimensionsInput
          label="Gebäudemaße Wandbereich"
          fieldName="wallBuildingDimensions"
          labelColor="#105ba4"
        />
        <BuildingDimensionsInput
          label="Gebäudemaße Dachbereich"
          fieldName="roofBuildingDimensions"
          labelColor="#e32f27"
        />
        {!isSimplifiedMethod && (
          <BuildingDimensionsInput
            label="Gebäudemaße Q-Zonen"
            fieldName="qZoneBuildingDimensions"
            labelColor="black"
          />
        )}
      </Stack>
      <Divider textAlign="center">Parameter</Divider>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={6}>
          <TextField
            select
            label="Windzone"
            name={`baseParameters.wind_zone`}
            onChange={event => {
              const nextWindZone = event.target.value as unknown as WindZone
              setValue('baseParameters.wind_zone', nextWindZone)
              if (isSimplifiedMethod) {
                const nextStandort = normalizeSimplifiedMethodStandort(
                  nextWindZone,
                  simplifiedMethodStandort,
                )
                setValue('baseParameters.simplifiedMethodStandort', nextStandort, {
                  shouldDirty: true,
                })
                const buildingHeightForQpLookup = baseParameters?.buildingHeightFor_q_p_lookup ?? 10
                applySimplifiedMethodQpProposal(
                  nextWindZone,
                  buildingHeightForQpLookup,
                  nextStandort,
                )
              } else {
                setValue('baseParameters.wind_speed', windZoneToSpeedProposal[nextWindZone])
                setValue(
                  'baseParameters.baseWindPressure_q_b',
                  windZoneToPressureProposal[nextWindZone],
                )
              }
            }}
            size="small"
            data-cy="select-wind-zone"
          >
            {Object.entries(windZoneOptions).map(([key, { value, label }]) => (
              <MenuItem value={value} key={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {isSimplifiedMethod ? (
          <>
            <Grid item xs={6}>
              <Select
                label="Standort"
                name="baseParameters.simplifiedMethodStandort"
                options={standortOptions}
                data-cy="select-simplified-method-standort"
                onChange={event => {
                  const nextStandort = event.target.value as SimplifiedMethodStandort
                  setValue('baseParameters.simplifiedMethodStandort', nextStandort, {
                    shouldDirty: true,
                  })
                  const buildingHeightForQpLookup =
                    baseParameters?.buildingHeightFor_q_p_lookup ?? 10
                  applySimplifiedMethodQpProposal(windZone, buildingHeightForQpLookup, nextStandort)
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <UnitConvertedField
                label={
                  <span>
                    Böengeschwindigkeitsdruck q<sub>p</sub>
                  </span>
                }
                name="baseParameters.gustPressure_q_p"
                type="number"
                unitLabel="kN/m²"
                size="small"
                data-cy="input-wind-speed-pressure-q-p"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={
                  <span>
                    Gebäudehöhe h für q<sub>p</sub>
                  </span>
                }
                name="baseParameters.buildingHeightFor_q_p_lookup"
                data-cy="input-building-height-for-q-p-lookup"
                type="number"
                onChange={event => {
                  const buildingHeightForQpLookup = Number(event.target.value)
                  setValue(
                    'baseParameters.buildingHeightFor_q_p_lookup',
                    buildingHeightForQpLookup,
                    { shouldDirty: true },
                  )
                  if (!Number.isNaN(buildingHeightForQpLookup) && buildingHeightForQpLookup > 0) {
                    applySimplifiedMethodQpProposal(
                      windZone,
                      buildingHeightForQpLookup,
                      normalizeSimplifiedMethodStandort(windZone, simplifiedMethodStandort),
                    )
                  }
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                }}
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={6}>
              <Select
                label="Geländekategorie"
                name="baseParameters.terrain_category"
                options={terrainCategoryOptions}
                data-cy="select-terrain-category"
              />
            </Grid>
            <Grid item xs={6}>
              <UnitConvertedField
                label={
                  <span>
                    Basisgeschw.druck q<sub>b</sub>
                  </span>
                }
                name="baseParameters.baseWindPressure_q_b"
                type="number"
                unitLabel="kN/m²"
                size="small"
                data-cy="input-wind-pressure"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={
                  <span>
                    Windgeschwindigkeit v<sub>b</sub>
                  </span>
                }
                name="baseParameters.wind_speed"
                data-cy="input-wind-speed"
                type="number"
                InputProps={{
                  endAdornment: <InputAdornment position="end">m/s</InputAdornment>,
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}

export default BaseParameterFields
